import type { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { getQuestionByID, resolveQuestionBySlug } from '@api-client/api-client';
import {
  questionByUuidKey,
  resolveQuestionKey,
  upsertQuestionEntities,
} from '@lib/cache/questions';
import type { PollRead } from '@models/api/poll';
import { pollForRead, pollReadMark, showPoll } from './pollWriteOrder';

/** Reads per poll end: the first at `closes_at`, then after each wait below. */
export const POLL_REREAD_MAX_TRIES = 5;
/** The wait after each read that did not end it: 1, 2, 4, 8 minutes. */
export const POLL_REREAD_BACKOFF_MS = [60_000, 120_000, 240_000, 480_000] as const;

interface RereadGate {
  tries: number;
  /** No read before this time. */
  nextAtMs: number;
  inFlight: boolean;
  /** A 403 or 404: the viewer can no longer read it, so asking again changes nothing. */
  stopped: boolean;
}

// One gate per poll end, shared by every card showing it (the feed behind the Post sheet and the
// sheet itself), keyed by the end time so a poll reopened with a new one gets its own tries.
const gates = new Map<string, RereadGate>();

const gateKey = (id: string, closesAt: string | null) => `${id}|${closesAt ?? ''}`;

/** Clears every gate; module state outlives a test otherwise. */
export const resetPollRereadsForTests = (): void => {
  gates.clear();
};

const isRefusal = (error: unknown): boolean =>
  isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 404);

/**
 * Reads one poll's question once and writes its block into every cached copy: through the post
 * page's own read (by slug, which a guest may make; by uuid when there is no slug), so a read
 * already in flight for it is shared. Unless a vote, end or reopen started after the read went
 * out (or is still in flight): then the block on screen is kept and the read's stale copy is put
 * right (`pollForRead`). Resolves with the read's own block; rejects as the read does.
 */
export const readPollQuestion = async (
  queryClient: QueryClient,
  question: { id: string; slug: string | null },
): Promise<PollRead | null> => {
  const { id, slug } = question;
  const mark = pollReadMark(id);
  const read = slug
    ? await queryClient.fetchQuery({
        queryKey: resolveQuestionKey(slug),
        queryFn: async ({ signal }) => (await resolveQuestionBySlug(slug, { signal })).data,
        staleTime: 0,
      })
    : await queryClient.fetchQuery({
        queryKey: questionByUuidKey(id),
        queryFn: async ({ signal }) => (await getQuestionByID(id, { signal })).data,
        staleTime: 0,
      });
  const poll = read.poll ? pollForRead(id, mark, read.poll) : null;
  upsertQuestionEntities(queryClient, [poll ? { ...read, poll } : read]);
  if (poll) showPoll(queryClient, read.id, poll);
  return read.poll ?? null;
};

/**
 * Reads one poll again when its end time passes (TECH-197 § Time and ending: "At the end the card
 * re-reads the poll and shows the result without a reload"). One question, never a feed: through
 * the post page's own read (by slug, which a guest may make; by uuid when there is no slug), so a
 * read already in flight for it is shared, then into every cached copy.
 *
 * Capped: the clock asks every tick while the card is open on screen, so a server whose clock is
 * behind, or a failing read, would otherwise be asked once a minute forever. At most
 * `POLL_REREAD_MAX_TRIES` reads per poll end, `POLL_REREAD_BACKOFF_MS` apart; a 403 or 404 stops
 * them. Asks inside the wait are dropped.
 */
export const rereadPollQuestion = async (
  queryClient: QueryClient,
  question: { id: string; slug: string | null; closesAt: string | null },
): Promise<void> => {
  const { id, slug, closesAt } = question;
  const key = gateKey(id, closesAt);
  const now = Date.now();
  const gate = gates.get(key) ?? { tries: 0, nextAtMs: 0, inFlight: false, stopped: false };
  gates.set(key, gate);
  if (gate.stopped || gate.inFlight || gate.tries >= POLL_REREAD_MAX_TRIES || now < gate.nextAtMs) {
    return;
  }
  gate.tries += 1;
  gate.inFlight = true;
  // The wait counts from this read going out.
  gate.nextAtMs = now + (POLL_REREAD_BACKOFF_MS[gate.tries - 1] ?? Number.POSITIVE_INFINITY);
  try {
    const poll = await readPollQuestion(queryClient, { id, slug });
    // Ended (or moved to another end time): this end's gate has done its job.
    if (poll && (poll.is_closed || poll.closes_at !== closesAt)) gates.delete(key);
  } catch (error) {
    // A refusal stops; anything else waits for the next allowed try.
    if (isRefusal(error)) gate.stopped = true;
  } finally {
    gate.inFlight = false;
  }
};
